/**
 * Lawity Context
 * Central state for Context-Aware UI
 *
 * Combines:
 * - Bio State (Oura Ring / health data)
 * - Location State (GPS / office detection)
 * - Matter State (current matter context)
 * - UI Preferences (derived from above)
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';

// ============================================
// TYPE DEFINITIONS
// ============================================

export type StressLevel = 'low' | 'moderate' | 'high' | 'critical';
export type EnergyLevel = 'depleted' | 'low' | 'normal' | 'high';

export interface BioState {
  stress: StressLevel;
  energy: EnergyLevel;
  sleepScore: number; // 0-100
  readinessScore: number; // 0-100
  hrv: number; // Heart Rate Variability
  lastUpdated: Date | null;
  isConnected: boolean;
}

export type LocationType = 'office' | 'court' | 'home' | 'travel' | 'unknown';

export interface LocationState {
  type: LocationType;
  coordinates?: { lat: number; lng: number };
  timezone: string;
  isWorkHours: boolean;
  lastUpdated: Date | null;
}

export type MatterStage =
  | 'intake'
  | 'discovery'
  | 'negotiation'
  | 'litigation'
  | 'closing'
  | 'archived';

export interface MatterState {
  id: string | null;
  name: string | null;
  stage: MatterStage | null;
  clientId: string | null;
  clientName: string | null;
  urgentTasks: number;
  unreadMessages: number;
  nextDeadline: Date | null;
}

export type SuggestedLayout = 'focus' | 'kanban' | 'timeline' | 'analytics' | 'default' | 'calendar' | 'clients' | 'billing';

export interface UIPreferences {
  /** Force dark mode when stress is high */
  shouldForceDarkMode: boolean;
  /** Reduce animations when energy is low */
  shouldReduceMotion: boolean;
  /** Suggested dashboard layout based on context */
  suggestedLayout: SuggestedLayout;
  /** Show simplified UI when stressed */
  simplifiedMode: boolean;
  /** Audio briefing autoplay preference */
  autoplayBriefing: boolean;
}

export interface LawityContextState {
  // States
  bio: BioState;
  location: LocationState;
  matter: MatterState;
  ui: UIPreferences;

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Actions
  setBioState: (bio: Partial<BioState>) => void;
  setLocationState: (location: Partial<LocationState>) => void;
  setMatterState: (matter: Partial<MatterState>) => void;
  refreshBioData: () => Promise<void>;
  refreshLocation: () => Promise<void>;
  clearMatter: () => void;

  // Command palette state
  isCommandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;

  // Help modal state
  isHelpOpen: boolean;
  setHelpOpen: (open: boolean) => void;

  // Audio player state
  isAudioPlaying: boolean;
  setAudioPlaying: (playing: boolean) => void;
  isZenMode: boolean;
  setZenMode: (zen: boolean) => void;
}

// ============================================
// DEFAULT VALUES
// ============================================

const defaultBioState: BioState = {
  stress: 'moderate',
  energy: 'normal',
  sleepScore: 75,
  readinessScore: 70,
  hrv: 45,
  lastUpdated: null,
  isConnected: false,
};

const defaultLocationState: LocationState = {
  type: 'unknown',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  isWorkHours: true,
  lastUpdated: null,
};

const defaultMatterState: MatterState = {
  id: null,
  name: null,
  stage: null,
  clientId: null,
  clientName: null,
  urgentTasks: 0,
  unreadMessages: 0,
  nextDeadline: null,
};

// ============================================
// CONTEXT
// ============================================

const LawityContext = createContext<LawityContextState | null>(null);

// ============================================
// HELPER FUNCTIONS
// ============================================

function deriveSuggestedLayout(
  bio: BioState,
  matter: MatterState,
  location: LocationState
): SuggestedLayout {
  // High stress = Focus Mode (single task, no distractions)
  if (bio.stress === 'high' || bio.stress === 'critical') {
    return 'focus';
  }

  // At court = Timeline view for dockets
  if (location.type === 'court') {
    return 'timeline';
  }

  // Matter stage dictates layout
  if (matter.stage) {
    switch (matter.stage) {
      case 'closing':
        return 'kanban';
      case 'litigation':
      case 'discovery':
        return 'timeline';
      case 'intake':
      case 'negotiation':
        return 'default';
      default:
        return 'analytics';
    }
  }

  return 'default';
}

function deriveUIPreferences(
  bio: BioState,
  location: LocationState,
  matter: MatterState
): UIPreferences {
  const isStressed = bio.stress === 'high' || bio.stress === 'critical';
  const isLowEnergy = bio.energy === 'depleted' || bio.energy === 'low';

  return {
    shouldForceDarkMode: isStressed,
    shouldReduceMotion: isLowEnergy,
    suggestedLayout: deriveSuggestedLayout(bio, matter, location),
    simplifiedMode: isStressed || isLowEnergy,
    autoplayBriefing: location.isWorkHours && bio.energy !== 'depleted',
  };
}

// ============================================
// PROVIDER
// ============================================

interface LawityProviderProps {
  children: ReactNode;
  initialBio?: Partial<BioState>;
  initialLocation?: Partial<LocationState>;
  initialMatter?: Partial<MatterState>;
}

export function LawityProvider({
  children,
  initialBio,
  initialLocation,
  initialMatter,
}: LawityProviderProps) {
  // Core states
  const [bio, setBio] = useState<BioState>({ ...defaultBioState, ...initialBio });
  const [location, setLocation] = useState<LocationState>({
    ...defaultLocationState,
    ...initialLocation,
  });
  const [matter, setMatter] = useState<MatterState>({
    ...defaultMatterState,
    ...initialMatter,
  });

  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCommandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [isHelpOpen, setHelpOpen] = useState(false);
  const [isAudioPlaying, setAudioPlaying] = useState(false);
  const [isZenMode, setZenMode] = useState(false);

  // Derived UI preferences
  const ui = useMemo(
    () => deriveUIPreferences(bio, location, matter),
    [bio, location, matter]
  );

  // State updaters
  const setBioState = useCallback((updates: Partial<BioState>) => {
    setBio((prev) => ({ ...prev, ...updates, lastUpdated: new Date() }));
  }, []);

  const setLocationState = useCallback((updates: Partial<LocationState>) => {
    setLocation((prev) => ({ ...prev, ...updates, lastUpdated: new Date() }));
  }, []);

  const setMatterState = useCallback((updates: Partial<MatterState>) => {
    setMatter((prev) => ({ ...prev, ...updates }));
  }, []);

  const clearMatter = useCallback(() => {
    setMatter(defaultMatterState);
  }, []);

  // Refresh bio data from Oura API
  const refreshBioData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // In production, call Oura Ring API
      // const response = await fetch('/api/oura/daily-readiness');
      // const data = await response.json();
      // setBioState(data);

      // Mock implementation
      console.log('Bio data refresh requested');
      setBioState({
        lastUpdated: new Date(),
        isConnected: true,
      });
    } catch (err) {
      setError('Failed to refresh bio data');
      console.error('Failed to refresh bio data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [setBioState]);

  // Refresh location
  const refreshLocation = useCallback(async () => {
    if (!('geolocation' in navigator)) {
      setLocationState({ type: 'unknown' });
      return;
    }

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      setLocationState({
        coordinates: {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        },
        // In production, reverse geocode to determine location type
        type: 'office',
        lastUpdated: new Date(),
      });
    } catch {
      setLocationState({ type: 'unknown' });
    }
  }, [setLocationState]);

  // Auto-detect location on mount
  useEffect(() => {
    refreshLocation();
  }, [refreshLocation]);

  // Check work hours periodically
  useEffect(() => {
    const checkWorkHours = () => {
      const now = new Date();
      const hour = now.getHours();
      const day = now.getDay();

      // Work hours: Mon-Fri, 8am-6pm
      const isWeekday = day >= 1 && day <= 5;
      const isDuringHours = hour >= 8 && hour < 18;

      setLocationState({ isWorkHours: isWeekday && isDuringHours });
    };

    checkWorkHours();
    const interval = setInterval(checkWorkHours, 60000);

    return () => clearInterval(interval);
  }, [setLocationState]);

  // Apply dark mode when stressed
  useEffect(() => {
    if (ui.shouldForceDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, [ui.shouldForceDarkMode]);

  // Apply reduced motion preference
  useEffect(() => {
    if (ui.shouldReduceMotion) {
      document.documentElement.classList.add('reduce-motion');
    } else {
      document.documentElement.classList.remove('reduce-motion');
    }
  }, [ui.shouldReduceMotion]);

  const value: LawityContextState = {
    bio,
    location,
    matter,
    ui,
    isLoading,
    error,
    setBioState,
    setLocationState,
    setMatterState,
    refreshBioData,
    refreshLocation,
    clearMatter,
    isCommandPaletteOpen,
    setCommandPaletteOpen,
    isHelpOpen,
    setHelpOpen,
    isAudioPlaying,
    setAudioPlaying,
    isZenMode,
    setZenMode,
  };

  return (
    <LawityContext.Provider value={value}>
      {children}
    </LawityContext.Provider>
  );
}

// ============================================
// HOOKS
// ============================================

export function useLawityContext(): LawityContextState {
  const context = useContext(LawityContext);
  if (!context) {
    throw new Error('useLawityContext must be used within a LawityProvider');
  }
  return context;
}

export function useBioState() {
  const { bio, setBioState, refreshBioData } = useLawityContext();
  return { bio, setBioState, refreshBioData };
}

export function useLocationState() {
  const { location, setLocationState, refreshLocation } = useLawityContext();
  return { location, setLocationState, refreshLocation };
}

export function useMatterState() {
  const { matter, setMatterState, clearMatter } = useLawityContext();
  return { matter, setMatterState, clearMatter };
}

export function useUIPreferences() {
  const { ui } = useLawityContext();
  return ui;
}

export function useCommandPalette() {
  const { isCommandPaletteOpen, setCommandPaletteOpen } = useLawityContext();
  return {
    isOpen: isCommandPaletteOpen,
    open: () => setCommandPaletteOpen(true),
    close: () => setCommandPaletteOpen(false),
    toggle: () => setCommandPaletteOpen(!isCommandPaletteOpen),
  };
}

export function useAudioPlayer() {
  const {
    isAudioPlaying,
    setAudioPlaying,
    isZenMode,
    setZenMode,
  } = useLawityContext();

  return {
    isPlaying: isAudioPlaying,
    play: () => setAudioPlaying(true),
    pause: () => setAudioPlaying(false),
    toggle: () => setAudioPlaying(!isAudioPlaying),
    isZenMode,
    enterZenMode: () => setZenMode(true),
    exitZenMode: () => setZenMode(false),
    toggleZenMode: () => setZenMode(!isZenMode),
  };
}

export default LawityContext;
